import {
  BarsArrowUpIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { Alert, AlertActions, AlertBody } from '@/primitives/alert.tsx';
import { useFetchVisibleAccountsQuery } from '@/slices/account-api-slice.ts';
import type { AccountListModel } from '../types/account.type.ts';
import { FrequencyEnum } from '../types/account-category.type.ts';
import { type CalendarEvent, EventTypeEnum } from '../types/event.type.ts';
import {
  type RepresentativeModel,
  representativeHasValidSubscription,
} from '../types/representative.type.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../types/staff-member.type.ts';
import { UserTypeEnum } from '../types/user-type.ts';
import { useAuth } from './auth-provider.tsx';

interface SecurityContext {
  meetingRequest: {
    showRequestButton: (calendarEvent: CalendarEvent) => boolean;
    handleRequestMeeting: (calendarEvent: CalendarEvent) => void;
  };
  meetingDetails: {
    showDetailButton: (calendarEvent: CalendarEvent) => boolean;
  };
  navItems: NavBarItem[];
  currentAccount: AccountListModel;
  setCurrentAccount: (account: AccountListModel | null) => void;
  visibleAccounts: AccountListModel[];
}

export interface NavBarItem {
  label: string;
  url: string;
  icon?: any;
  group?: string;
}

// common nav items
const dashboardNavItem: NavBarItem = { label: 'Dashboard', url: '/dashboard' };
const calendarNavItem: NavBarItem = { label: 'Calendar', url: '/calendar' };
const chatNavItem: NavBarItem = { label: 'Chat', url: '/chat-home' };

// representative nav items
const connectedAccountsNavItem: NavBarItem = { label: 'Accounts', url: '/connected-accounts' };

// organization admin nav items
const organizationAccountsNavItem: NavBarItem = { label: 'Accounts', url: '/account' };

// site admin nav items
const siteAdminNavItem: NavBarItem = { label: 'Admin', url: '/admin' };

// staff member and MeetingMD admin nav items
const staffMembersNavItem: NavBarItem = { label: 'Staff', url: '/staff' };
const representativesNavItem: NavBarItem = { label: 'Reps', url: '/representative' };

// MeetingMD admin nav items
const adminOrganizationsNavItem: NavBarItem = {
  group: 'admin',
  label: 'Organizations',
  url: '/organization',
  icon: <BuildingOfficeIcon />,
};

const adminUsersNavItem: NavBarItem = {
  group: 'admin',
  label: 'Admin Users',
  url: '/admin-user-list',
  icon: <UserGroupIcon />,
};

const onboardingNavItem: NavBarItem = {
  group: 'admin',
  label: 'Onboarding',
  url: '/onboard-import',
  icon: <BarsArrowUpIcon />,
};

export const SecurityContext = createContext<SecurityContext | undefined>(undefined);

const MeetingAlert = ({
  message,
  isOpen,
  setIsOpen,
}: {
  message: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => (
  <Alert onClose={setIsOpen} open={isOpen}>
    <AlertBody>{message}</AlertBody>
    <AlertActions>
      <Button variant='light' onClick={() => setIsOpen(false)}>
        <XMarkIcon />
        Close Window
      </Button>
    </AlertActions>
  </Alert>
);

export const SecurityProvider = ({ children }) => {
  const navigate = useNavigate();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const { user, isImpersonating } = useAuth();
  const [navItems, setNavItems] = useState<NavBarItem[]>([]);

  const { purgeKeys } = usePageLoading();
  const { pathname } = useLocation();
  const [currentAccount, setCurrentAccount] = useState<AccountListModel | null>(null);
  const { data: accounts } = useFetchVisibleAccountsQuery(user ? {} : skipToken);

  const accountStaffNavItems = (user: StaffMemberModel, account: AccountListModel) => {
    const roleId = _(user?.accountSiteStaffMembers ?? [])
      .filter(x => (account ? x.accountId === account.id : true))
      .orderBy(x => x.staffRole.id, 'desc')
      .head()?.staffRole?.id;

    const navItems = [dashboardNavItem];
    if (roleId >= StaffRoleEnum.OrganizationAdministrator) {
      navItems.push(organizationAccountsNavItem);
    }

    navItems.push(calendarNavItem);

    if (roleId >= StaffRoleEnum.Manager) {
      navItems.push(staffMembersNavItem);
    }

    if (roleId >= StaffRoleEnum.SiteAdministrator) {
      navItems.push(representativesNavItem, siteAdminNavItem);
    }

    if (roleId >= StaffRoleEnum.User) {
      navItems.push(chatNavItem);
    }
    return navItems;
  };

  useEffect(() => {
    switch (user?.userType) {
      case UserTypeEnum.DrugRep: {
        const rep = user as RepresentativeModel;
        const hasSubscription = representativeHasValidSubscription(rep);
        const shouldSkipSubscriptionCheck = isImpersonating;

        if (!hasSubscription && !shouldSkipSubscriptionCheck) {
          purgeKeys();
          setNavItems([]);
          if (!['/subscription', '/support'].includes(pathname)) {
            if (!sessionStorage.getItem('redirectAfterSubscription')) {
              sessionStorage.setItem('redirectAfterSubscription', pathname);
            }
            navigate('/subscription');
          }
        } else {
          setNavItems([dashboardNavItem, calendarNavItem, connectedAccountsNavItem, chatNavItem]);
        }
        break;
      }
      case UserTypeEnum.Admin:
        setNavItems([
          dashboardNavItem,
          representativesNavItem,
          staffMembersNavItem,
          calendarNavItem,
          chatNavItem,

          // admin menu
          adminOrganizationsNavItem,
          adminUsersNavItem,
          onboardingNavItem,
        ]);
        break;
      default:
        setNavItems(accountStaffNavItems(user as StaffMemberModel, currentAccount));
    }
  }, [user, pathname, currentAccount, isImpersonating]);

  const handleRequestMeeting = (event: CalendarEvent) => {
    const frequency =
      event.representative.frequencyOverrideId ?? event.representative.accountCategoryFrequencyId;
    let nextAvailableDate = event.lastScheduledAppointment
      ? DateTime.fromISO(event.lastScheduledAppointment)
      : DateTime.now().minus({ year: 10 });
    const nextAppointment = DateTime.fromISO(event.nextScheduledAppointment);
    let nextAppointmentConflict = false;
    switch (frequency) {
      case FrequencyEnum.Weekly:
        nextAvailableDate = nextAvailableDate.plus({ days: 7 });
        nextAppointmentConflict =
          nextAppointment && nextAppointment.diff(nextAvailableDate).days <= 7;
        break;
      case FrequencyEnum.Monthly:
        nextAvailableDate = nextAvailableDate.plus({ month: 1 });
        nextAppointmentConflict =
          nextAppointment && nextAppointment.diff(nextAvailableDate).months <= 1;
        break;
      case FrequencyEnum.Quarterly:
        nextAvailableDate = nextAvailableDate.plus({ month: 3 });
        nextAppointmentConflict =
          nextAppointment && nextAppointment.diff(nextAvailableDate).months <= 3;
        break;
      case FrequencyEnum.SemiAnnually:
        nextAvailableDate = nextAvailableDate.plus({ month: 6 });
        nextAppointmentConflict =
          nextAppointment && nextAppointment.diff(nextAvailableDate).months <= 6;
        break;
      case FrequencyEnum.Annually:
        nextAvailableDate = nextAvailableDate.plus({ year: 1 });
        nextAppointmentConflict =
          nextAppointment && nextAppointment.diff(nextAvailableDate).years <= 1;
        break;
      case FrequencyEnum.Unlimited:
        navigate(`/meeting-request/${event.key}`);
        return;
    }

    if (frequency === FrequencyEnum.Never) {
      setAlertMessage(
        'Until further notice you are not eligible to schedule a meeting with this account.',
      );
      setAlertOpen(true);
    } else if (nextAppointmentConflict) {
      setAlertMessage(
        `This meeting time would cause your next meeting to fall outside your regularly scheduled events. Your next meeting is set for ${formatDate(nextAppointment, DateTime.DATE_FULL)}`,
      );
      setAlertOpen(true);
    } else if (nextAvailableDate >= DateTime.fromISO(event.start)) {
      const message = `This meeting time falls outside your regularly scheduled events. You are eligible to schedule your next meeting on or after ${formatDate(nextAvailableDate, DateTime.DATE_FULL)}`;
      setAlertMessage(message);
      setAlertOpen(true);
    } else {
      navigate(`/meeting-request/${event.key}`);
    }
  };

  const showMeetingRequestButton = (event: CalendarEvent) => {
    return event.canRequest && event.eventType === EventTypeEnum.AvailabilitySlot;
  };

  const showMeetingDetailButton = (event: CalendarEvent) => {
    return event.eventType === EventTypeEnum.MeetingEntry && event.canView;
  };

  const contextValue: SecurityContext = {
    meetingRequest: {
      showRequestButton: showMeetingRequestButton,
      handleRequestMeeting: handleRequestMeeting,
    },
    meetingDetails: {
      showDetailButton: showMeetingDetailButton,
    },
    navItems: navItems,
    currentAccount: currentAccount,
    setCurrentAccount: setCurrentAccount,
    visibleAccounts: accounts,
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
      <MeetingAlert setIsOpen={setAlertOpen} message={alertMessage} isOpen={alertOpen} />
    </SecurityContext.Provider>
  );
};

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('Must be used within SecurityContext');
  }
  return context;
};
